<template>
  <div class="shop-detail">
    <div class="back-btn" @click="goBack">← 返回上一页</div>

    <div v-if="loading" class="loading-state">加载中...</div>

    <div v-else>
      <div class="detail-header">
        <h2>店家详情</h2>
      </div>

      <div class="detail-section">
        <h3>基本信息</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>店家ID</label>
            <span>{{ shopDetail.id }}</span>
          </div>
          <div class="info-item">
            <label>店家名称</label>
            <span>{{ shopDetail.name }}</span>
          </div>
          <div class="info-item">
            <label>联系人</label>
            <span>{{ shopDetail.contactName }}</span>
          </div>
          <div class="info-item">
            <label>手机号</label>
            <span>{{ shopDetail.phone }}</span>
          </div>
          <div class="info-item">
            <label>所属小区</label>
            <span>{{ shopDetail.community }}</span>
          </div>
          <div class="info-item">
            <label>详细地址</label>
            <span>{{ shopDetail.address }}</span>
          </div>
          <div class="info-item">
            <label>物品数</label>
            <span>{{ products.length }}</span>
          </div>
          <div class="info-item">
            <label>订单数</label>
            <span>{{ shopDetail.orderCount }}</span>
          </div>
          <div class="info-item">
            <label>店家钱包余额</label>
            <span>{{ shopDetail.wallet ? (shopDetail.wallet.balance / 100).toFixed(2) : '0.00' }}</span>
          </div>
          <div class="info-item">
            <label>累计充值</label>
            <span>{{ shopDetail.wallet ? (shopDetail.wallet.total_recharge / 100).toFixed(2) : '0.00' }}</span>
          </div>
          <div class="info-item">
            <label>累计消费</label>
            <span>{{ shopDetail.wallet ? (shopDetail.wallet.total_consume / 100).toFixed(2) : '0.00' }}</span>
          </div>
          <div class="info-item">
            <label>累计退款</label>
            <span>{{ shopDetail.wallet ? (shopDetail.wallet.total_refund / 100).toFixed(2) : '0.00' }}</span>
          </div>
          <div class="info-item">
            <label>认证状态</label>
            <div class="status-row">
              <span :class="'cert-status cert-status-' + shopDetail.certStatus">{{ getCertStatusName(shopDetail.certStatus) }}</span>
              <button @click="approve" class="btn-success" v-if="shopDetail.certStatus < 6">←已完成此步骤，下一步</button>
              <button @click="reject" class="btn-danger" v-if="shopDetail.certStatus < 6">拒绝申请</button>
              <button @click="openCertModal" class="btn-primary">修改认证状态</button>
            </div>
          </div>
          <div class="info-item">
            <label>账号状态</label>
            <div class="status-row">
              <span :class="['status', shopDetail.status === 1 ? 'is-on' : 'is-off']">{{ shopDetail.status === 1 ? '正常' : '禁用' }}</span>
              <button type="button" class="btn-link status-toggle-btn" @click="toggleStatus">{{ shopDetail.status === 1 ? '禁用' : '启用' }}</button>
            </div>
          </div>
          <div class="info-item">
            <label>创建时间</label>
            <span>{{ formatDateShort(shopDetail.createdAt) }}</span>
          </div>
          <div class="info-item">
            <div class="info-item-head">
              <label>店家推广人</label>
            </div>
            <div class="shop-referrer-line">
              <span>{{ shopReferrerDisplay || '未设置' }}</span>
              <button type="button" class="btn-link shop-referrer-edit-btn" @click="openReferrerModal">编辑</button>
            </div>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3>操作</h3>
        <div class="action-buttons">
          <button @click="goEdit" class="btn-primary">修改资料</button>
          <button @click="openCertModal" class="btn-primary">修改认证状态</button>
          <button @click="toggleStatus" :class="shopDetail.status === 1 ? 'btn-danger' : 'btn-success'">
            {{ shopDetail.status === 1 ? '禁用店家' : '启用店家' }}
          </button>
          <button @click="showAddProductModal = true" class="btn-add">+ 添加物品</button>
          <button @click="openAdjustBalance" class="btn-balance">编辑余额</button>
        </div>
      </div>

      <div class="detail-section">
        <div class="section-header">
          <h3>店家钱包流水</h3>
          <router-link v-if="shopDetail.userId" :to="`/finance/transactions?user_id=${shopDetail.userId}&role_code=shop`" class="btn-link">查看全部角色钱包</router-link>
        </div>
        <transaction-list :user-id="shopDetail.userId" :role-code="'shop'" />
      </div>

      <div class="detail-section">
        <div class="section-header">
          <h3>物品列表</h3>
          <button @click="showAddProductModal = true" class="btn-add-small">+ 添加物品</button>
        </div>
        <div class="product-list" v-if="products.length">
          <div class="product-item" v-for="product in products" :key="product.id">
            <div class="product-thumb">
              <img v-if="product.image" :src="product.image" :alt="product.name" />
              <div v-else class="product-thumb-placeholder">无图</div>
            </div>
            <div class="product-info">
              <div class="product-name">{{ product.name }}</div>
              <div v-if="product.skus && product.skus.length" class="sku-wrap">
                <div class="sku-toggle" @click="toggleSkus(product)">
                  SKU（{{ product.skus.length }}）
                  <span class="sku-caret">{{ product._showSkus ? '收起' : '展开' }}</span>
                </div>
                <div v-if="product._showSkus" class="sku-list">
                  <div v-for="sku in product.skus" :key="sku.id" class="sku-row">
                    <span v-if="sku.is_default" class="sku-default">默认</span>
                    <span class="sku-name">{{ skuName(sku) }}</span>
                    <span class="sku-price">{{ (sku.sale_price / 100).toFixed(2) }}</span>
                    <span v-if="sku.code" class="sku-code">货号：{{ sku.code }}</span>
                    <span :class="['sku-status', sku.status === 1 ? 'is-on' : 'is-off']">{{ sku.status === 1 ? '上架' : '下架' }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="product-actions">
              <button @click="editProduct(product)" class="btn-edit">编辑</button>
              <button @click="deleteProduct(product)" class="btn-delete">删除</button>
              <button @click="openSkuModal(product)" class="btn-sku">添加SKU</button>
            </div>
          </div>
        </div>
        <div class="product-empty" v-else>暂无物品</div>
      </div>

      <!-- 添加物品弹窗 -->
      <div v-if="showAddProductModal" class="modal-overlay" @click.self="showAddProductModal = false">
        <div class="modal-content">
          <h3>添加物品</h3>
          <div class="form-group">
            <label>所属店家 *</label>
            <input type="text" :value="shopDetail.name" disabled class="disabled-input" />
          </div>
          <div class="form-group">
            <label>分类</label>
            <select v-model="newProduct.category_id">
              <option :value="0">请选择分类</option>
              <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>物品名称 *</label>
            <input type="text" v-model="newProduct.name" placeholder="请输入物品名称" />
          </div>
          <div class="form-group">
            <label>规格</label>
            <input type="text" v-model="newProduct.spec" placeholder="如：500g/份" />
          </div>
          <div class="form-group">
            <label>单位</label>
            <input type="text" v-model="newProduct.unit" placeholder="如：500g/份" />
          </div>
          <div class="form-group">
            <label>物品图片</label>
            <input type="text" v-model="newProduct.image" placeholder="请输入图片URL" />
          </div>
          <div class="form-group">
            <label>热门推荐</label>
            <select v-model="newProduct.is_hot">
              <option :value="0">否</option>
              <option :value="1">是</option>
            </select>
          </div>
          <div class="form-group">
            <label>物品描述</label>
            <textarea v-model="newProduct.description" placeholder="请输入物品描述" rows="3"></textarea>
          </div>
          <div class="modal-buttons">
            <button @click="showAddProductModal = false" class="btn-cancel">取消</button>
            <button @click="submitAddProduct" class="btn-submit">确认添加</button>
          </div>
        </div>
      </div>

      <!-- 添加 SKU 弹窗 -->
      <div v-if="showSkuModal" class="modal-overlay" @click.self="closeSkuModal">
        <div class="modal-content">
          <h3>为「{{ skuEditingProduct?.name }}」添加 SKU</h3>
          <div class="form-group">
            <label>规格（按规格维度逐项选择）</label>
            <SkuSpecSelector :product-id="skuEditingProduct.id" v-model="skuForm.specValueIds" />
          </div>
          <div class="form-group">
            <label>店家自定义货号</label>
            <input v-model="skuForm.code" type="text" placeholder="货号 / 外部编码" />
          </div>
          <div class="form-group">
            <label>SKU 图片URL</label>
            <input v-model="skuForm.image" type="text" placeholder="请输入图片地址" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>参考售价（元）</label>
              <input v-model.number="skuSalePriceYuan" type="number" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>每单位搬运费（元）</label>
              <input v-model.number="skuCarryFeeYuan" type="number" min="0" step="0.01" placeholder="单位为元" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>步梯每层楼层费（元）</label>
              <input v-model.number="skuFloorFeeYuan" type="number" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>是否默认 SKU</label>
              <select v-model.number="skuForm.is_default">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>累计销量</label>
              <input v-model.number="skuForm.sales_count" type="number" min="0" disabled />
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="skuForm.status">
                <option :value="1">上架</option>
                <option :value="0">下架</option>
              </select>
            </div>
          </div>
          <div class="modal-buttons">
            <button @click="closeSkuModal" class="btn-cancel">取消</button>
            <button @click="saveSku" class="btn-submit">保存</button>
          </div>
        </div>
      </div>

      <!-- 编辑余额弹窗 -->
      <div v-if="showAdjustModal" class="modal-overlay" @click.self="closeAdjustBalance">
        <div class="modal-content">
          <h3>编辑余额 - {{ shopDetail.name }}</h3>
          <div class="form-group">
            <label>当前余额（元）</label>
            <div class="form-control-static">{{ shopDetail.wallet ? (shopDetail.wallet.balance / 100).toFixed(2) : '0.00' }}</div>
          </div>
          <div class="form-group">
            <label>操作类型</label>
            <div class="radio-group">
              <label class="radio-item">
                <input type="radio" value="recharge" v-model="adjustForm.type" /> 充值
              </label>
              <label class="radio-item">
                <input type="radio" value="deduct" v-model="adjustForm.type" /> 扣除
              </label>
            </div>
          </div>
          <div class="form-group">
            <label>金额（单位：元）</label>
            <input type="number" min="0.01" step="0.01" v-model="adjustForm.amount" placeholder="请输入金额（单位：元）" />
          </div>
          <div class="form-group">
            <label>备注</label>
            <textarea v-model="adjustForm.description" placeholder="请输入备注" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>当前操作员</label>
            <input type="text" :value="currentOperatorName" class="disabled-input" readonly />
          </div>
          <p v-if="adjustError" class="form-error">{{ adjustError }}</p>
          <div class="modal-buttons">
            <button @click="closeAdjustBalance" class="btn-cancel">取消</button>
            <button :disabled="adjustSubmitting" @click="submitAdjustBalance" class="btn-submit">
              {{ adjustSubmitting ? '提交中...' : '确定' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="certModalVisible" class="modal-overlay" @click="closeCertModal">
      <div class="modal-content cert-modal" @click.stop>
        <div class="modal-header">
          <h3>修改认证状态</h3>
          <span class="close-btn" @click="closeCertModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>认证状态</label>
            <select v-model="certForm.certStatus" class="form-select">
              <option v-for="opt in certStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="form-item" v-if="certForm.certStatus === 7">
            <label>拒绝理由</label>
            <textarea v-model="certForm.certRemark" class="form-textarea" placeholder="请输入拒绝理由"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCertModal" class="btn-cancel">取消</button>
          <button @click="submitCertStatus" class="btn-submit">确认修改</button>
        </div>
      </div>
    </div>

    <!-- 店家推广人弹窗 -->
    <div v-if="referrerModalVisible" class="modal-overlay" @click="closeReferrerModal">
      <div class="modal-content community-modal" @click.stop>
        <div class="modal-header">
          <h3>修改店家推广人</h3>
          <span class="close-btn" @click="closeReferrerModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>当前店家推广人</label>
            <span class="readonly-text">{{ shopReferrerDisplay || '未设置' }}</span>
          </div>
          <div class="form-item">
            <input v-model="referrerSearch" class="form-input" placeholder="搜索推广人姓名/手机号" />
          </div>
          <div class="community-check-list">
            <label class="community-check-item">
              <input type="radio" :value="null" v-model="selectedReferrerId" />
              <span>不关联店家推广人</span>
            </label>
            <label v-for="opt in filteredReferrerOptions" :key="opt.id" class="community-check-item">
              <input type="radio" :value="opt.id" v-model="selectedReferrerId" />
              <span>{{ shopReferrerOptionName(opt) }}（{{ opt.user && opt.user.phone ? opt.user.phone : '-' }}）</span>
            </label>
            <div v-if="filteredReferrerOptions.length === 0" class="empty-tip">暂无店家推广人</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeReferrerModal" class="btn-cancel">取消</button>
          <button @click="submitReferrer" class="btn-submit" :disabled="submittingReferrer">
            {{ submittingReferrer ? '提交中...' : '确认修改' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getShopDetail, updateShop, adjustShopBalance, approveShop, rejectShop, getShopReferrerOptions, getShopReferrerRelations, addShopReferrer, removeShopReferrer } from '@/api/shops'
import { getProductList, createProduct, createSku } from '@/api/products'
import SkuSpecSelector from '@/components/product/SkuSpecSelector.vue'
import { getSpecValueOptions, getSkuSpecs, createSkuSpec } from '@/api/specs'
import { buildSkuName, skuSpecListToValueIds } from '@/utils/skuSpec'
import { getCategoryList } from '@/api/categories'
import { formatDateShort } from '@/utils/date'
import TransactionList from '@/components/TransactionList.vue'

export default {
  name: 'ShopDetail',
  components: {
    TransactionList,
    SkuSpecSelector
  },
  data() {
    return {
      loading: false,
      shopDetail: {},
      products: [],
      specValueMap: {},
      showAddProductModal: false,
      showSkuModal: false,
      skuEditingProduct: null,
      skuForm: {
        code: '',
        specValueIds: [],
        image: '',
        sale_price: 0,
        carry_fee: 10,
        floor_fee: 0,
        sales_count: 0,
        status: 1,
        is_default: 0
      },
      categoryOptions: [],
      newProduct: {
        name: '',
        shop_id: 0,
        category_id: 0,
        spec: '',
        unit: '',
        image: '',
        is_hot: 0,
        description: ''
      },
      showAdjustModal: false,
      adjustForm: {
        type: 'recharge',
        amount: 0.00,
        description: ''
      },
      adjustSubmitting: false,
      adjustError: '',
      certModalVisible: false,
      certForm: {
        certStatus: 0,
        certRemark: ''
      },
      certStatusOptions: [
        { value: 0, label: '已提交申请待邀约面谈' },
        { value: 1, label: '已邀约待面谈' },
        { value: 2, label: '已面谈待核验原件' },
        { value: 3, label: '已核验证件原件待提交照片' },
        { value: 4, label: '已提交证件照片待核验照片' },
        { value: 5, label: '已核验证件照片待签约' },
        { value: 6, label: '已签约' },
        { value: 7, label: '已拒绝' }
      ],
      referrerModalVisible: false,
      currentReferrerRelation: null,
      referrerOptions: [],
      referrerSearch: '',
      selectedReferrerId: null,
      submittingReferrer: false
    }
  },
  mounted() {
    this.loadShopDetail()
    this.loadCategories()
  },
  watch: {
    'shopDetail.id'(id) {
      if (id) {
        this.loadProducts(id)
      }
    }
  },
  computed: {
    // SKU 价格以「元」展示/编辑，内部 skuForm 仍以「分」保存
    skuSalePriceYuan: {
      get() {
        return this.skuForm.sale_price / 100
      },
      set(val) {
        this.skuForm.sale_price = Math.round(Number(val || 0) * 100)
      }
    },
    skuCarryFeeYuan: {
      get() {
        return this.skuForm.carry_fee / 100
      },
      set(val) {
        this.skuForm.carry_fee = Math.round(Number(val || 0) * 100)
      }
    },
    skuFloorFeeYuan: {
      get() {
        return this.skuForm.floor_fee / 100
      },
      set(val) {
        this.skuForm.floor_fee = Math.round(Number(val || 0) * 100)
      }
    },
    currentOperatorName() {
      const user = this.$store.state.user.user
      if (!user) return ''
      return user.real_name || user.username || ''
    },
    shopReferrerDisplay() {
      if (!this.currentReferrerRelation) return ''
      return this.shopReferrerName(this.currentReferrerRelation)
    },
    filteredReferrerOptions() {
      const kw = (this.referrerSearch || '').trim().toLowerCase()
      return (this.referrerOptions || []).filter(opt => {
        if (!kw) return true
        const name = (this.shopReferrerOptionName(opt) || '').toLowerCase()
        const phone = opt.user && opt.user.phone ? String(opt.user.phone).toLowerCase() : ''
        return name.includes(kw) || phone.includes(kw)
      })
    }
  },
  methods: {
    formatDateShort,
    async loadShopDetail() {
      try {
        this.loading = true
        const id = this.$route.params.id

        const response = await getShopDetail(id)
        if (response && response.data) {
          const data = response.data
          this.shopDetail = {
            id: data.id,
            userId: data.user_id || 0,
            name: data.name || '',
            contactName: data.contact_name || '',
            phone: data.phone || '',
            community: data.community?.name || data.community || '',
            createdAt: data.created_at || '',
            orderCount: data.total_orders || 0,
            wallet: data.wallet || null,
			address: data.address || '',
            status: data.status || 0,
            certStatus: data.cert_status || 0,
            certRemark: data.cert_remark || ''
          }
          this.loadReferrers(data.id)
        }
      } catch (error) {
        alert('加载店家详情失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.back()
    },
    async loadProducts(shopId) {
      try {
        const response = await getProductList({ shop_id: shopId })
        if (response && response.data) {
          // 处理分页数据：实际数组在 response.data.data 中
          const list = response.data.data || response.data || []
          const raw = Array.isArray(list) ? list : (list.data || [])
          this.products = raw.map(p => ({ ...p, _showSkus: true }))
        }
      } catch (error) {
      }
    },
    toggleSkus(product) {
      const next = !product._showSkus
      this.$set(product, '_showSkus', next)
      if (next) {
        this.loadProductSkuSpecs(product)
      }
    },
    async loadProductSkuSpecs(product) {
      if (!product.skus || !product.skus.length) return
      if (!Object.keys(this.specValueMap).length) {
        try {
          const res = await getSpecValueOptions()
          const list = (res && res.data && res.data.spec_values) || []
          const map = {}
          list.forEach(v => { map[v.id] = v })
          this.specValueMap = map
        } catch (e) {
          this.specValueMap = {}
        }
      }
      await Promise.all(product.skus.map(async (sku) => {
        try {
          const res = await getSkuSpecs(sku.id)
          const items = (res && res.data && res.data.list) || []
          this.$set(sku, '_specValueIds', skuSpecListToValueIds(items))
        } catch (e) {
          this.$set(sku, '_specValueIds', [])
        }
      }))
    },
    skuName(sku) {
      const name = buildSkuName(sku._specValueIds || [], this.specValueMap)
      return name || (sku.code ? `货号:${sku.code}` : '未选规格')
    },
    goEdit() {
      this.$router.push(`/user/shops/edit/${this.shopDetail.id}`)
    },
    async toggleStatus() {
      const action = this.shopDetail.status === 1 ? '禁用' : '启用'
      if (confirm(`确定要${action}店家 ${this.shopDetail.name} 吗？`)) {
        try {
          const newStatus = this.shopDetail.status === 1 ? 0 : 1
          await updateShop(this.shopDetail.id, { status: newStatus })
          this.shopDetail.status = newStatus
          alert(`${action}成功`)
        } catch (error) {
          alert('更新状态失败，请稍后重试')
        }
      }
    },
    openAdjustBalance() {
      this.adjustForm = {
        type: 'recharge',
        amount: 0.00,
        description: ''
      }
      this.adjustError = ''
      this.showAdjustModal = true
    },
    closeAdjustBalance() {
      this.showAdjustModal = false
    },
    getCertStatusName(status) {
      const map = {
        0: '已提交申请待邀约面谈',
        1: '已邀约待面谈',
        2: '已面谈待核验原件',
        3: '已核验证件原件待提交照片',
        4: '已提交证件照片待核验照片',
        5: '已核验证件照片待签约',
        6: '已签约',
        7: '已拒绝'
      }
      return map[status] || '未知'
    },
    openCertModal() {
      this.certForm.certStatus = this.shopDetail.certStatus || 0
      this.certForm.certRemark = this.shopDetail.certRemark || ''
      this.certModalVisible = true
    },
    closeCertModal() {
      this.certModalVisible = false
    },
    async submitCertStatus() {
      if (this.certForm.certStatus === 7 && !this.certForm.certRemark.trim()) {
        alert('请输入拒绝理由')
        return
      }
      if (confirm('确定要修改认证状态吗？')) {
        try {
          const res = await updateShop(this.shopDetail.id, {
            cert_status: this.certForm.certStatus,
            cert_remark: this.certForm.certStatus === 7 ? this.certForm.certRemark : ''
          })
          const updated = res && res.data && res.data.data ? res.data.data : null
          if (updated && updated.cert_status !== undefined) {
            this.shopDetail.certStatus = updated.cert_status
            this.shopDetail.certRemark = updated.cert_remark || ''
          } else {
            this.loadShopDetail()
          }
          this.closeCertModal()
          alert('认证状态修改成功')
        } catch (error) {
          alert('操作失败，请稍后重试')
        }
      }
    },
    async approve() {
      if (confirm('确定已完成此步骤，进入下一步吗？')) {
        try {
          const res = await approveShop(this.shopDetail.id)
          if (res && res.code === 0) {
            alert(res.message || '已进入下一步')
            this.loadShopDetail()
          } else {
            alert((res && res.message) || '操作失败')
          }
        } catch (error) {
          alert('操作失败，请稍后重试')
        }
      }
    },
    async reject() {
      const remark = prompt('请输入拒绝理由：')
      if (remark) {
        try {
          const res = await rejectShop(this.shopDetail.id, remark)
          if (res && res.code === 0) {
            alert('拒绝认证成功')
            this.loadShopDetail()
          } else {
            alert((res && res.message) || '操作失败')
          }
        } catch (error) {
          alert('操作失败，请稍后重试')
        }
      }
    },
    async submitAdjustBalance() {
      this.adjustError = ''
      const amount = parseFloat(this.adjustForm.amount)
      if (!amount || amount <= 0) {
        this.adjustError = '请输入大于 0 的金额'
        return
      }
      this.adjustSubmitting = true
      try {
        const res = await adjustShopBalance(this.shopDetail.id, {
          type: this.adjustForm.type,
          // 单位保持「元」，由后端统一转换为「分」后写入
          amount: amount,
          description: this.adjustForm.description
        })
        const newBalance = res && res.data ? res.data.balance : null
        if (newBalance !== null && newBalance !== undefined && this.shopDetail.wallet) {
          this.shopDetail.wallet.balance = newBalance
        } else {
          this.loadShopDetail()
        }
        this.showAdjustModal = false
        alert('操作成功')
      } catch (error) {
        this.adjustError = (error.response && error.response.data && error.response.data.message) || '操作失败，请重试'
      } finally {
        this.adjustSubmitting = false
      }
    },
    async loadCategories() {
      try {
        const response = await getCategoryList()
        if (response && response.data) {
          // 处理分页数据：实际数组在 response.data.data 中
          const categories = response.data.data || response.data || []
          this.categoryOptions = Array.isArray(categories) ? categories.filter(cat => cat && cat.id) : []
        }
      } catch (error) {
      }
    },
    async submitAddProduct() {
      if (!this.newProduct.name) {
        alert('请输入物品名称')
        return
      }
      
      try {
        const productData = {
          shop_id: this.shopDetail.id,
          name: this.newProduct.name,
          category_id: this.newProduct.category_id || null,
          spec: this.newProduct.spec || '',
          unit: this.newProduct.unit || '',
          image: this.newProduct.image || '',
          is_hot: this.newProduct.is_hot,
          description: this.newProduct.description || '',
          status: 1
        }
        
        await createProduct(productData)
        alert('添加物品成功')
        this.showAddProductModal = false
        this.loadProducts(this.shopDetail.id)
        // 重置表单
        this.newProduct = {
          shop_id: this.shopDetail.id,
          name: '',
          category_id: 0,
          spec: '',
          unit: '',
          image: '',
          is_hot: 0,
          description: ''
        }
        // 跳转到物品列表
        // this.$router.push('/products/list')
      } catch (error) {
        alert('添加物品失败，请稍后重试')
      }
    },
    openSkuModal(product) {
      this.skuEditingProduct = product
      this.skuForm = {
        code: '',
        specValueIds: [],
        image: '',
        sale_price: 0,
        carry_fee: 10,
        floor_fee: 0,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
      this.showSkuModal = true
    },
    closeSkuModal() {
      this.showSkuModal = false
      this.skuEditingProduct = null
      this.skuForm = {
        code: '',
        specValueIds: [],
        image: '',
        sale_price: 0,
        carry_fee: 10,
        floor_fee: 0,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
    },
    async saveSku() {
      const payload = { ...this.skuForm }
      delete payload.specValueIds
      try {
        const productId = this.skuEditingProduct.id
        const response = await createSku(productId, payload)
        const newSku = response.data.data || response.data
        if (newSku && newSku.id !== undefined) {
          await Promise.all((this.skuForm.specValueIds || []).map(id => createSkuSpec(newSku.id, id)))
        }
        this.skuEditingProduct.sku_count = (this.skuEditingProduct.sku_count || 0) + 1
        alert('SKU 添加成功')
        this.closeSkuModal()
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'SKU 添加失败'
        alert(message)
      }
    },
    async loadReferrers(shopId) {
      if (!shopId) return
      try {
        const res = await getShopReferrerRelations(shopId)
        const list = (res && res.code === 0 && res.data) ? (res.data.list || []) : []
        // 一个店家只关联一个推广人，取第一条关系
        this.currentReferrerRelation = list.length ? list[0] : null
      } catch (error) {
        this.currentReferrerRelation = null
      }
    },
    shopReferrerName(r) {
      if (!r) return '未设置'
      const referrer = r.shop_referrer || r.shopReferrer || r
      const user = referrer.user || referrer
      // 优先展示真实姓名，其次为昵称/用户名
      const realName = user && (user.real_name || user.profile?.real_name)
      const fallback = user && (user.name || user.username)
      return (realName || fallback) || '未设置'
    },
    shopReferrerOptionName(opt) {
      if (!opt) return '未设置'
      const user = opt.user || opt
      const realName = user && (user.real_name || user.profile?.real_name)
      const fallback = user && (user.name || user.username)
      return (realName || fallback) || '未设置'
    },
    async openReferrerModal() {
      try {
        const res = await getShopReferrerOptions({ page: 1, pageSize: 100 })
        this.referrerOptions = (res && res.code === 0 && res.data) ? (res.data.list || []) : []
      } catch (error) {
        this.referrerOptions = []
        alert('加载店家推广人列表失败')
      }
      this.selectedReferrerId = (this.currentReferrerRelation && this.currentReferrerRelation.shop_referrer_id) || null
      this.referrerSearch = ''
      this.referrerModalVisible = true
    },
    closeReferrerModal() {
      this.referrerModalVisible = false
    },
    async submitReferrer() {
      const currentId = this.currentReferrerRelation ? this.currentReferrerRelation.shop_referrer_id : null
      const newId = this.selectedReferrerId
      if (currentId === newId) {
        this.closeReferrerModal()
        return
      }
      if (confirm('确定要修改该店家的推广人吗？')) {
        try {
          this.submittingReferrer = true
          // 若已有关系且要变更/清除，先移除旧关系
          if (this.currentReferrerRelation && (newId === null || newId !== currentId)) {
            const delRes = await removeShopReferrer(this.currentReferrerRelation.id)
            if (!(delRes && delRes.code === 0)) {
              alert((delRes && delRes.message) || '移除原推广人失败')
              return
            }
          }
          // 添加新的推广人关系
          if (newId !== null) {
            const addRes = await addShopReferrer(this.shopDetail.id, newId)
            if (!(addRes && addRes.code === 0)) {
              alert((addRes && addRes.message) || '操作失败')
              return
            }
          }
          alert('店家推广人修改成功')
          await this.loadReferrers(this.shopDetail.id)
          this.closeReferrerModal()
        } catch (error) {
          alert('操作失败，请稍后重试')
        } finally {
          this.submittingReferrer = false
        }
      }
    }
  }
}
</script>

<style scoped>
.shop-detail {
  padding: 20px;
}

.back-btn {
  color: #3498db;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 14px;
}

.detail-header {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.detail-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-section h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.section-header h3 {
  margin: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.btn-link {
  align-self: flex-start;
  margin-top: 8px;
  padding: 4px 10px;
  background: transparent;
  color: #3498db;
  border: 1px solid #3498db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
}

.btn-link:hover {
  background: #3498db;
  color: #fff;
}

.btn-add-small {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  background: #3498db;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-add-small:hover {
  background: #2980b9;
}

/* 物品列表 */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: #fafbfc;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  transition: box-shadow 0.2s, transform 0.2s;
}

.product-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.product-thumb {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-thumb-placeholder {
  font-size: 12px;
  color: #bbb;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-price {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #e74c3c;
}

.product-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.product-actions button {
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: opacity 0.2s;
}

.product-actions button:hover {
  opacity: 0.85;
}

/* SKU 列表 */
.sku-wrap {
  margin-top: 8px;
}

.sku-toggle {
  display: inline-block;
  font-size: 12px;
  color: #3498db;
  cursor: pointer;
  user-select: none;
}

.sku-caret {
  color: #999;
}

.sku-list {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sku-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  color: #555;
}

.sku-default {
  padding: 1px 6px;
  border-radius: 4px;
  background: #e8f5e9;
  color: #2ecc71;
  font-size: 11px;
}

.sku-name {
  font-weight: 500;
  color: #333;
}

.sku-specs {
  color: #888;
}

.sku-price {
  color: #e74c3c;
  font-weight: 600;
}

.sku-code {
  color: #999;
}

.sku-status {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.sku-status.is-on { background: #e8f5e9; color: #2ecc71; }
.sku-status.is-off { background: #ffebee; color: #e74c3c; }

.btn-edit {
  background: #3498db;
  color: #fff;
}

.btn-delete {
  background: #e74c3c;
  color: #fff;
}

.btn-sku {
  background: #9b59b6;
  color: #fff;
}

.product-empty {
  padding: 30px;
  text-align: center;
  color: #999;
  font-size: 14px;
  background: #fafbfc;
  border: 1px dashed #eee;
  border-radius: 10px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status.is-on { background: #e8f5e9; color: #2ecc71; }
.status.is-off { background: #ffebee; color: #e74c3c; }

.btn-link {
  align-self: flex-start;
  margin-top: 8px;
  padding: 4px 10px;
  background: transparent;
  color: #3498db;
  border: 1px solid #3498db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
}

.status-toggle-btn {
  margin: 0;
}

.btn-link:hover {
  background: #3498db;
  color: #fff;
}

.cert-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.cert-status-0 { background: #f5f5f5; color: #666; }
.cert-status-1 { background: #fff3e0; color: #f39c12; }
.cert-status-2 { background: #e8f5e9; color: #2ecc71; }
.cert-status-3 { background: #ffebee; color: #e74c3c; }
.cert-status-4 { background: #f3e5f5; color: #9b59b6; }
.cert-status-5 { background: #fffde7; color: #f1c40f; }
.cert-status-6 { background: #e8f5e9; color: #2ecc71; }
.cert-status-7 { background: #ffebee; color: #e74c3c; }

.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.action-buttons {
  display: flex;
  gap: 15px;
}

.action-buttons button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  padding: 10px 24px;
  background: #f39c12;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-danger {
  padding: 10px 24px;
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-success {
  padding: 10px 24px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.loading-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.btn-add {
  background: #3498db;
  color: #fff;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  width: 400px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group-half {
  flex: 1;
}

.form-group textarea{
  width: 350px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
}

/* 认证状态弹窗（对齐跑手详情弹窗样式） */
.cert-modal {
  padding: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
  border-bottom: none;
  padding-bottom: 0;
}

.close-btn {
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-item {
  margin-bottom: 15px;
}

.form-item label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.form-select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.disabled-input {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.modal-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel {
  padding: 8px 20px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
}

.btn-submit {
  padding: 8px 20px;
  border: none;
  background: #3498db;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f5f5f5;
}

.btn-submit:hover {
  background: #2980b9;
}

.btn-balance {
  background: #f39c12;
  color: #fff;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  cursor: pointer;
}

.form-control-static {
  font-size: 18px;
  font-weight: 600;
  color: #e67e22;
}

.form-error {
  color: #e74c3c;
  font-size: 13px;
  margin: 0 0 8px;
}

.info-item-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
}

.info-item-head label {
  margin-bottom: 0;
}

.shop-referrer-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.shop-referrer-edit-btn {
  margin: 0;
  align-self: center;
  padding: 2px 10px;
  font-size: 12px;
  white-space: nowrap;
}

.community-modal {
  width: 480px;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.community-check-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.community-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.readonly-text {
  font-size: 14px;
  color: #333;
}

.empty-tip {
  font-size: 13px;
  color: #999;
  text-align: center;
  padding: 20px 0;
}

.community-delete-btn {
  margin-top: 0;
  padding: 2px 10px;
  font-size: 12px;
  color: #e74c3c;
  border-color: #e74c3c;
}

.community-delete-btn:hover {
  background: #e74c3c;
  color: #fff;
}
</style>